/* eslint-disable tailwindcss/no-arbitrary-value */
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {
  useContext, useEffect, useMemo, useState, useRef
} from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  DrawerBody,
  DrawerFooter,
  LoadingButton,
  Typography,
  Alert,
  TextField, IconButton, Tooltip, Link
} from '@one-thd/sui-atomic-components';
import { Info } from '@one-thd/sui-icons';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import {
  extend, useLazyDataModel,
} from '@thd-nucleus/data-sources';
import { GroupedIntents } from './GroupedIntents';
import { AddToContext } from '../../AddToContext';
import { SCREENS, TRACK } from '../../AddToConstants';
import { Feedback } from './Feedback';
import {
  generateId,
  getCurrentIntents,
  getSelectedMaterials,
  triggerAnalytics
} from '../../AddToHelpers';
import { useListMetrics } from '../../hooks/useListMetrics';

export const IntentsPreview = ({
  onNewIntentsSave, refId, refType,
}) => {
  const [showAddGroupInput, setShowAddGroupInput] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const { channel } = useContext(ExperienceContext);
  const accept = useRef(false);
  const isProjects = refType === 'PROJECT';

  const {
    groupedIntentsPreview,
    generatedIntents,
    setGroupedIntentsPreview,
    setScreen,
    handleClose,
    previousScreen,
    mcvisId,
    storeId
  } = useContext(AddToContext);

  const {
    groupedIntents, sessionId = '', sourceType, starterListName, aiInputMethod, aiInputType
  } = groupedIntentsPreview || {};

  const [submitFeedback, { loading, data, error }] = useLazyDataModel('interactWithAgent', {
    variables: {
      interactionType: 'FEEDBACK',
      metaData: {
        sessionId,
        channel,
        refType,
        refId,
        mcvis: mcvisId || '',
      }
    },
    context: {
      withAuth: true
    }
  });

  const listDataForMetrics = refId && storeId ? { id: refId, storeId } : {};
  const { myListInformation } = useListMetrics([], listDataForMetrics);

  useEffect(() => {
    // 10.4 Customize Your List Overlay - Track when customer lands on the overlay
    triggerAnalytics(TRACK.LIST_BUILDER_CUSTOMIZE_LIST_OVERLAY, {
      aiSessionId: sessionId
    });

    return () => {
      if (!accept.current) {
        setGroupedIntentsPreview({ ...groupedIntentsPreview, groupedIntents: [] });
        // If the user navigates away without accepting, send discard feedback
        const selectedMaterials = getSelectedMaterials(groupedIntents);
        const currentIntents = getCurrentIntents(generatedIntents, selectedMaterials);
        accept.current = false;
        submitFeedback({
          variables: {
            feedbackData: {
              type: null,
              accept_discard: 'DISCARD',
              category: null,
              comment: null,
              currentIntents
            }
          }
        });
      }
    };
  }, []);

  useEffect(() => {
    if ((data || error) && !loading && accept.current) {
      const selectedMaterials = getSelectedMaterials(groupedIntents);
      if (onNewIntentsSave) {
        onNewIntentsSave(selectedMaterials).finally(() => {
          handleClose();
        });
      }
    }
  }, [data, error, loading]);

  const count = useMemo(() => {
    let selected = 0;
    let all = 0;
    (groupedIntents || []).forEach((group) => {
      (group.materials || []).forEach((item) => {
        all += 1;
        if (item.isSelected) {
          selected += 1;
        }
      });
    });
    return { selected, all };
  }, [groupedIntents]);

  const onItemUpdate = (groupId, id, updatedFields) => {
    // Find the group and update the item
    setGroupedIntentsPreview((prevIntents) => {
      return {
        ...prevIntents,
        groupedIntents: (prevIntents?.groupedIntents || []).map((group) => {
          if (group.id === groupId) {
            const itemExists = group.materials.some((item) => item.id === id);
            if (!itemExists) {
              // If the item doesn't exist, add it with the updated fields
              return {
                ...group,
                materials: [...group.materials, { id, ...updatedFields }]
              };
            }
            return {
              ...group,
              materials: group.materials.map((item) => {
                if (item.id === id) {
                  return { ...item, ...updatedFields };
                }
                return item;
              })
            };
          }
          return group;
        })
      };
    });
  };

  const onBulkItemUpdate = (groupId, updatedItems) => {
    // Find the group and update all items
    setGroupedIntentsPreview((prevIntents) => {
      return {
        ...prevIntents,
        groupedIntents: (prevIntents?.groupedIntents || []).map((group) => {
          if (group.id === groupId) {
            return {
              ...group,
              materials: group.materials.map((item) => {
                if (updatedItems[item.id]) {
                  return { ...item, ...updatedItems[item.id] };
                }
                return item;
              })
            };
          }
          return group;
        })
      };
    });
  };

  const onAddGroup = () => {
    setShowAddGroupInput(true);
  };

  const handleSaveGroup = () => {
    const newGroupTrimmed = newGroupName.trim();
    if (newGroupTrimmed) {
      const newGroupId = generateId();
      const newGroup = {
        id: newGroupId,
        groupName: newGroupTrimmed,
        itemOrder: 1,
        materials: [],
        __typename: 'MaterialsGroup'
      };

      // Add the new group at the beginning and update itemOrder for existing groups
      setGroupedIntentsPreview((prevIntents) => {
        const updatedExistingGroups = (prevIntents?.groupedIntents || []).map((group) => ({
          ...group,
          itemOrder: group.itemOrder + 1
        }));
        return {
          ...prevIntents,
          groupedIntents: [newGroup, ...updatedExistingGroups]
        };
      });
    }

    // Reset state and hide input
    setNewGroupName('');
    setShowAddGroupInput(false);
  };

  const handleCancelAddGroup = () => {
    setNewGroupName('');
    setShowAddGroupInput(false);
  };

  const handleSelectAll = () => {
    const unselect = count.all === count.selected;
    setGroupedIntentsPreview((prevIntents) => {
      return {
        ...prevIntents,
        groupedIntents: (prevIntents?.groupedIntents || []).map((group) => {
          return {
            ...group,
            materials: group.materials.map((item) => ({
              ...item,
              isSelected: !unselect
            }))
          };
        })
      };
    });
  };

  const handleAccept = async () => {
    try {
      const selectedMaterials = getSelectedMaterials(groupedIntents);
      const currentIntents = getCurrentIntents(generatedIntents, selectedMaterials);

      // 6.13.1 Add to List action - Use metrics from existing list items only
      if (selectedMaterials && selectedMaterials.length > 0) {
        const { uniqueSkuCount, quantityTotal, listTotal } = myListInformation;

        triggerAnalytics(TRACK.ADD_TO_LIST_ADD_ACTION, {
          name: 'list add',
          location: 'INTENTS_PREVIEW',
          items: selectedMaterials.map((material) => ({
            quantity: material.quantity || 1,
            price: 0,
            sku: material.id,
            intentStatus: 'yes',
            resolvedFromIntent: 'no'
          })),
          isBulkAction: selectedMaterials.length > 1,
          listTotal, // Only actual products
          uniqueSkuCount, // Only actual products with SKUs
          quantityTotal, // Only actual products with quantities
          // Pass raw analytics context to thd-analytics component
          sourceType,
          sessionId,
          starterListName,
          aiInputMethod,
          aiInputType
        });
      }

      accept.current = true;
      await submitFeedback({
        variables: {
          feedbackData: {
            type: null,
            accept_discard: 'ACCEPT',
            category: null,
            comment: null,
            currentIntents
          }
        }
      });
    } catch (errorCatched) {
      // eslint-disable-next-line no-console
      console.error('Error accepting intents:', errorCatched);
    }
  };

  return (
    <>
      <DrawerBody>
        <div className="sui-p-2 sui-grid sui-justify-items-end">
          <Alert status="info" size="large">
            Almost done. Organize items here, then confirm your final product selections on your list.
          </Alert>
          <div className="sui-flex sui-flex-row sui-mt-auto">
            <Tooltip
              placement="bottom-start"
              title={`Product list generated by AI; please verify against your job requirements.
               The Home Depot is not responsible for the accuracy of this product list.`}
            >
              <IconButton color="info" icon={Info} />
            </Tooltip>
            <div className="sui-pt-2">
              <Typography variant="body-xs" height="tight" paragraph>
                Product list generated by AI
              </Typography>
            </div>
          </div>
        </div>
        <div className="sui-mt-2 sui-px-2 sui-flex sui-flex-row sui-w-full">
          <div className="sui-flex sui-flex-col sui-gap-2 sui-w-[150px]">
            <Typography variant="body-base" weight="bold">{count.selected} Selected</Typography>
            <Link
              align="left"
              component="button"
              onClick={handleSelectAll}
            >
              {count.all === count.selected ? 'Unselect All' : 'Select All'}
            </Link>
          </div>
          <div
            className="sui-flex sui-justify-end sui-items-center sui-gap-2 sui-w-full"
          >
            {showAddGroupInput ? (
              <div className="sui-flex sui-w-full sui-flex-row sui-gap-2 sui-align-middle">
                <TextField
                  value={newGroupName}
                  onChange={(event) => setNewGroupName(event.target.value)}
                  placeholder="Enter group name"
                  autoFocus
                  fullWidth
                />
                <div className="sui-flex sui-justify-end sui-items-center sui-gap-2 sui-pt-2">
                  <Button
                    variant="primary"
                    size="small"
                    onClick={handleSaveGroup}
                  >
                    Save
                  </Button>
                  <Button
                    variant="ghost"
                    size="small"
                    onClick={handleCancelAddGroup}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="sui-mt-2">
                <Button
                  variant="ghost"
                  onClick={onAddGroup}
                >
                  + Add Group
                </Button>
              </div>
            )}
          </div>
        </div>
        <div className="sui-mt-2">
          {
            (groupedIntents || []).map((group) => (
              <div key={`group-${group.id}`} className="sui-mb-2">
                <GroupedIntents
                  group={group}
                  onItemUpdate={onItemUpdate}
                  onBulkItemUpdate={onBulkItemUpdate}
                />
              </div>
            ))
          }
        </div>
        <Feedback refId={refId} refType={refType} />
      </DrawerBody>
      <DrawerFooter
        position="center"
        style={{ boxShadow: '0 1px 8px 0 rgb(0 0 0 / 0.375)' }}
      >
        <div className="sui-flex sui-flex-col sui-justify-between sui-gap-2">
          <LoadingButton
            fullWidth
            variant="primary"
            value="Step 3: Save and Review Matches"
            loading={loading}
            onClick={handleAccept}
          >Step 3: Save and Review Matches
          </LoadingButton>
          <Button
            variant="secondary"
            fullWidth
            onClick={() => {
              // 10.4.1 Customize Your List Overlay Exit/Cancel - track when going back via Back button
              triggerAnalytics(TRACK.LIST_BUILDER_CUSTOMIZE_LIST_OVERLAY, {
                screen: SCREENS.GENERATED_LIST_INTENTS_PREVIEW
              });

              // Navigate back to the previous screen, or default to DEFAULT screen if no previous screen
              setScreen(previousScreen || SCREENS.DEFAULT);
            }}
          >Back
          </Button>
        </div>
      </DrawerFooter>
    </>
  );
};

IntentsPreview.displayName = 'IntentsPreview';

IntentsPreview.propTypes = {
  onNewIntentsSave: PropTypes.func.isRequired,
  refType: PropTypes.string,
  refId: PropTypes.string
};

IntentsPreview.defaultProps = {
  refId: '',
  refType: ''
};

IntentsPreview.dataModel = extend(
  Feedback,
  useListMetrics.dataModel
);
